import { useEffect, useRef } from "react";

export interface ToastData {
  id: number;
  message: string;
  kind: "success" | "error";
  duration: number;
}

export default function Toast({
  toast,
  onDismiss,
}: {
  toast: ToastData;
  onDismiss: (id: number) => void;
}) {
  const remainingRef = useRef<number>(toast.duration);
  const startRef = useRef<number>(Date.now());
  const timerRef = useRef<number | null>(null);

  const startTimer = (delay: number) => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
    }
    startRef.current = Date.now();
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      onDismiss(toast.id);
    }, delay);
  };

  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => {
    remainingRef.current = toast.duration;
    startTimer(toast.duration);
    return () => {
      clearTimer();
    };
  }, [toast.id, toast.duration]);

  const handleMouseEnter = () => {
    if (timerRef.current !== null) {
      const elapsed = Date.now() - startRef.current;
      remainingRef.current = Math.max(0, remainingRef.current - elapsed);
      clearTimer();
    }
  };

  const handleMouseLeave = () => {
    if (timerRef.current === null && remainingRef.current > 0) {
      startTimer(remainingRef.current);
    }
  };

  return (
    <div
      className={`toast toast--${toast.kind}`}
      role={toast.kind === "error" ? "alert" : "status"}
      onClick={() => onDismiss(toast.id)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span className="toast-message">{toast.message}</span>
      <button
        type="button"
        className="toast-close"
        aria-label="Dismiss notification"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss(toast.id);
        }}
      >
        ×
      </button>
    </div>
  );
}
